import { animate, useMotionValue, useReducedMotion, useTransform, m } from 'motion/react';
import { useEffect } from 'react';

/**
 * Una cifra que cuenta hasta su valor en vez de aparecer ya puesta.
 *
 * Sirve para dos cosas concretas en este producto: en el panel de empresa deja
 * claro qué contador ha cambiado al recargar, y en el de costes convierte una
 * cifra que crece sola en algo que se nota. Fuera de ahí sería ruido, así que
 * no está en `Card` ni en ningún sitio por defecto.
 *
 * `useMotionValue` y no estado de React: la cifra se actualiza sesenta veces
 * por segundo y hacerlo con `setState` repintaría el árbol entero en cada
 * fotograma. Aquí solo cambia el nodo de texto.
 *
 * `useReducedMotion` y no el `MotionConfig` de la raíz: `MotionConfig` gobierna
 * a los componentes `m.*`, pero esta animación la lanza `animate()` a mano y no
 * pasa por ahí. Sin consultarlo, quien pide menos movimiento —y todo test, que
 * corre con la preferencia activada— vería la cifra recorriendo su cuenta atrás
 * en lugar del valor.
 */
export function Counter({ value, format }: { value: number; format?: (value: number) => string }) {
  const reduced = useReducedMotion();
  const motion = useMotionValue(reduced ? value : 0);
  const text = useTransform(motion, (current) => (format ?? String)(Math.round(current)));

  useEffect(() => {
    if (reduced) {
      motion.set(value);

      return;
    }

    const controls = animate(motion, value, { duration: 0.7, ease: [0.22, 0.8, 0.28, 1] });

    return () => controls.stop();
  }, [motion, value, reduced]);

  return <m.span>{text}</m.span>;
}
